import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    if (this.hasAttribute('mobile-swipe-close')) {
      this.refs.dialog.addEventListener('touchstart', this.#handleSwipeStart, { passive: true, capture: true });
      this.refs.dialog.addEventListener('touchmove', this.#handleSwipeMove, { passive: false, capture: true });
      this.refs.dialog.addEventListener('touchend', this.#handleSwipeEnd, { passive: true, capture: true });
      this.refs.dialog.addEventListener('touchcancel', this.#handleSwipeCancel, { passive: true, capture: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    this.refs.dialog?.removeEventListener('touchstart', this.#handleSwipeStart, true);
    this.refs.dialog?.removeEventListener('touchmove', this.#handleSwipeMove, true);
    this.refs.dialog?.removeEventListener('touchend', this.#handleSwipeEnd, true);
    this.refs.dialog?.removeEventListener('touchcancel', this.#handleSwipeCancel, true);
    this.#swipeAnimation?.cancel();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {HTMLElement | null} */
  #trigger = null;

  #opening = false;

  #swipeClosing = false;

  /** @type {{ startX: number; startY: number; lastY: number; startTime: number; lastTime: number; active: boolean; cancelled: boolean } | null} */
  #swipeGesture = null;

  /** @type {Animation | null} */
  #swipeAnimation = null;

  /**
   * Shows the dialog.
   */
  async showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#opening) return;

    this.#opening = true;
    const activeElement = document.activeElement;
    const trigger =
      activeElement instanceof HTMLElement &&
      dialog.id &&
      activeElement.getAttribute('aria-controls') === dialog.id
        ? activeElement
        : null;

    if (this.hasAttribute('data-header-panel')) {
      await closeOtherHeaderSurfaces(this);
    }

    if (dialog.open) {
      this.#opening = false;
      return;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    this.#trigger = trigger;
    this.#trigger?.setAttribute('aria-expanded', 'true');

    // Prevent layout thrashing by separating DOM reads from DOM writes
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;

        dialog.showModal();
        this.dispatchEvent(new DialogOpenEvent());

        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
        this.#opening = false;
        resolve(undefined);
      });
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#swipeClosing) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    this.#finishDialogClose();
  };

  #finishDialogClose = () => {
    const { dialog } = this.refs;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    dialog.style.removeProperty('animation');
    dialog.style.removeProperty('transform');
    dialog.style.removeProperty('will-change');

    this.#swipeAnimation?.cancel();
    this.#swipeAnimation = null;
    this.#swipeGesture = null;
    this.#swipeClosing = false;

    const trigger = this.#trigger;
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
    this.#trigger = null;

    this.dispatchEvent(new DialogCloseEvent());
  };

  #handleSwipeStart = (event) => {
    const { dialog } = this.refs;
    if (
      !dialog.open ||
      this.#swipeClosing ||
      event.touches.length !== 1 ||
      !window.matchMedia('(max-width: 749px)').matches
    ) {
      return;
    }

    const target = event.composedPath()[0];
    if (!(target instanceof Element) || !target.closest('[data-mobile-swipe-handle]')) return;

    const touch = event.touches[0];
    this.#swipeGesture = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastY: touch.clientY,
      startTime: event.timeStamp,
      lastTime: event.timeStamp,
      active: false,
      cancelled: false,
    };
  };

  #handleSwipeMove = (event) => {
    const gesture = this.#swipeGesture;
    if (!gesture || gesture.cancelled || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    gesture.lastY = touch.clientY;
    gesture.lastTime = event.timeStamp;

    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 10 && Math.abs(deltaX) >= Math.abs(deltaY)) {
      gesture.cancelled = true;
      if (gesture.active) void this.#resetSwipePosition(Math.max(deltaY, 0));
      return;
    }

    if (deltaY < 0) {
      gesture.cancelled = true;
      if (gesture.active) void this.#resetSwipePosition(0);
      return;
    }

    if (deltaY < 10) return;

    gesture.active = true;
    event.preventDefault();

    const { dialog } = this.refs;
    dialog.style.animation = 'none';
    dialog.style.willChange = 'transform';
    dialog.style.transform = `translate3d(0, ${deltaY}px, 0)`;
  };

  #handleSwipeEnd = (event) => {
    const gesture = this.#swipeGesture;
    this.#swipeGesture = null;
    if (!gesture?.active || gesture.cancelled) return;

    const endY = event.changedTouches?.[0]?.clientY ?? gesture.lastY;
    const distance = Math.max(endY - gesture.startY, 0);
    const duration = Math.max((event.timeStamp || gesture.lastTime) - gesture.startTime, 1);
    const velocity = distance / duration;

    if (distance >= 96 || (distance >= 42 && velocity >= 0.5)) {
      void this.#closeFromSwipe(distance);
      return;
    }

    void this.#resetSwipePosition(distance);
  };

  #handleSwipeCancel = () => {
    const gesture = this.#swipeGesture;
    this.#swipeGesture = null;
    if (gesture?.active) void this.#resetSwipePosition(Math.max(gesture.lastY - gesture.startY, 0));
  };

  async #closeFromSwipe(distance) {
    const { dialog } = this.refs;
    if (this.#swipeClosing || !dialog.open) return;

    this.#swipeClosing = true;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishDialogClose();
      return;
    }

    const animation = dialog.animate(
      [
        { transform: `translate3d(0, ${distance}px, 0)` },
        { transform: 'translate3d(0, 100dvh, 0)' },
      ],
      { duration: 220, easing: 'cubic-bezier(0.55, 0, 1, 0.45)', fill: 'forwards' }
    );
    this.#swipeAnimation = animation;

    await Promise.race([
      animation.finished.catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, 320)),
    ]);
    this.#finishDialogClose();
  }

  async #resetSwipePosition(distance) {
    const { dialog } = this.refs;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.style.removeProperty('transform');
      dialog.style.removeProperty('will-change');
      return;
    }

    const animation = dialog.animate(
      [
        { transform: `translate3d(0, ${distance}px, 0)` },
        { transform: 'translate3d(0, 0, 0)' },
      ],
      { duration: 180, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );
    this.#swipeAnimation = animation;

    await Promise.race([
      animation.finished.catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, 260)),
    ]);

    if (this.#swipeAnimation === animation) {
      animation.cancel();
      this.#swipeAnimation = null;
      dialog.style.removeProperty('transform');
      dialog.style.removeProperty('will-change');
    }
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

/**
 * Close an existing mobile navigation, localization sheet, search modal or cart drawer
 * before another header panel becomes modal.
 * @param {DialogComponent | null} currentPanel
 */
async function closeOtherHeaderSurfaces(currentPanel) {
  /** @type {Array<Promise<unknown>>} */
  const closing = [];

  const headerDrawer = document.querySelector('header-drawer.simedge-mobile-menu-component');
  if (
    headerDrawer &&
    'close' in headerDrawer &&
    typeof headerDrawer.close === 'function' &&
    headerDrawer.querySelector(':scope > details[open]')
  ) {
    closing.push(Promise.resolve(headerDrawer.close()));
  }

  const localization = document.querySelector('simedge-mobile-localization');
  if (
    localization &&
    'close' in localization &&
    typeof localization.close === 'function' &&
    localization.querySelector('dialog[open]')
  ) {
    closing.push(Promise.resolve(localization.close()));
  }

  document.querySelectorAll('[data-header-panel]').forEach((panel) => {
    if (
      panel !== currentPanel &&
      panel instanceof DialogComponent &&
      panel.refs.dialog?.open
    ) {
      closing.push(Promise.resolve(panel.closeDialog()));
    }
  });

  await Promise.allSettled(closing);
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const activatesAccount = event
      .composedPath()
      .some((node) => node instanceof Element && node.matches('.account-button, shopify-account'));

    if (activatesAccount) void closeOtherHeaderSurfaces(null);
  },
  { capture: true }
);
